/* eslint-disable no-useless-catch */
import {apiClient} from './apiClient';
import {LoginRequest, LoginResponse, RegistrationRequest, TwoFactorVerificationRequest, UserInfo} from '../types/api';

/**
 * Authentication service for handling all auth-related API calls
 * Following the service layer pattern from best practices guide
 */
export class AuthService {
    /**
     * User login with email and password
     */
    static async login(credentials: LoginRequest): Promise<LoginResponse> {
        try {
            const response = await apiClient.post<any>('/api/login', credentials);

            // Handle legacy API response structure (response.result)
            const result = response.result || response;

            // Check if user is not verified - throw error to prevent login
            if (result.success === false && result.error === 'User not verified') {
                const error = new Error('User not verified');
                (error as any).response = {data: result};
                throw error;
            }

            // Also check if the success field is missing but we have an error field
            if (result.error === 'User not verified' && !result.token) {
                const error = new Error('User not verified');
                (error as any).response = {data: result};
                throw error;
            }

            // Set token in API client for subsequent requests
            if (result.token) {
                apiClient.setToken(result.token);
            }

            return result;
        } catch (error: any) {
            // Check if this is a 401 error with "User not verified" message
            if (error.status === 401 && error.data?.error === 'User not verified') {
                const newError = new Error('User not verified');
                (newError as any).response = {data: error.data};
                throw newError;
            }

            // Also check the error message in the response data
            if (error.status === 401 && error.data && typeof error.data === 'object') {
                // Check various possible error message locations
                const errorMessage = error.data.error || error.data.message || '';
                if (errorMessage === 'User not verified') {
                    const newError = new Error('User not verified');
                    (newError as any).response = {data: error.data};
                    throw newError;
                }
            }

            throw error;
        }
    }

    /**
     * User registration/account creation
     */
    static async register(userData: RegistrationRequest): Promise<{message: string}> {
        try {
            return await apiClient.post<{message: string}>('/api/register', userData);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Resend verification email for account activation
     */
    static async resendVerificationEmail(userData: RegistrationRequest): Promise<{message: string}> {
        try {
            return await apiClient.post<{message: string}>('/api/resend', userData);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Account verification using token
     */
    static async verifyAccount(token: string): Promise<LoginResponse> {
        try {
            const response = await apiClient.post<LoginResponse>('/api/auth', {token});

            if (response.token) {
                apiClient.setToken(response.token);
            }

            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Set password during account verification
     */
    static async setPassword(token: string, password: string): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>(`/api/verify/${token}`, {password});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Password reset request
     */
    static async requestPasswordReset(email: string): Promise<{message: string}> {
        try {
            return await apiClient.post<{message: string}>('/api/password-reset', {email});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Reset password with token
     */
    static async resetPassword(token: string, password: string): Promise<{message: string}> {
        try {
            return await apiClient.post<{message: string}>(`/api/reset-password/${token}`, {password});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Refresh authentication token using httpOnly cookie
     */
    static async refreshToken(): Promise<{token: string}> {
        try {
            const response = await apiClient.post<any>(
                '/api/refresh',
                {},
                {
                    withCredentials: true, // Send httpOnly refresh cookie
                }
            );

            // Handle backend response structure (response.result)
            const result = response.result || response;
            const newToken = result.token;

            if (newToken) {
                apiClient.setToken(newToken);
            }

            return {token: newToken};
        } catch (error) {
            throw error;
        }
    }

    /**
     * Logout user (clear token and all client-side data)
     */
    static async logout(): Promise<void> {
        try {
            // Call logout endpoint (CRITICAL: this should clear httpOnly refresh token cookies on server)
            await apiClient.post(
                '/api/logout',
                {},
                {
                    withCredentials: true, // Ensure cookies are sent so server can clear them
                }
            );
        } catch {
            // Continue with logout even if API call fails, but warn user
            // You might want to show a warning to the user here that they should close their browser
        } finally {
            // Always clear token from API client
            apiClient.clearToken();

            // Clear all client-side storage
            try {
                localStorage.clear();
                sessionStorage.clear();
            } catch {
                // Handle storage clearing errors silently
            }
        }
    }

    // Two-Factor Authentication Methods

    /**
     * Send SMS verification code
     */
    static async sendSmsVerificationCode(phone: string): Promise<{message: string}> {
        try {
            return await apiClient.post<{message: string}>('/api/2FA/SMS/verificationCode', {phone});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Verify SMS code
     */
    static async verifySmsCode(data: TwoFactorVerificationRequest): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>('/api/2FA/SMS/verify', data);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Send email verification code
     */
    static async sendEmailVerificationCode(email: string): Promise<{message: string}> {
        try {
            return await apiClient.post<{message: string}>('/api/2FA/email/verificationCode', {email});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Verify email code
     */
    static async verifyEmailCode(data: TwoFactorVerificationRequest): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>('/api/2FA/email/verify', data);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Verify TOTP code
     */
    static async verifyTotpCode(verificationCode: string): Promise<{success: boolean}> {
        try {
            return await apiClient.post<{success: boolean}>('/api/2FA/TOTP/verify', {verificationCode});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Enable two-factor authentication
     */
    static async enableTwoFactor(mode: 'SMS' | 'EMAIL' | 'TOTP'): Promise<{qrCode?: string; backupCodes?: string[]}> {
        try {
            return await apiClient.post<{qrCode?: string; backupCodes?: string[]}>('/api/2FA/enable', {mode});
        } catch (error) {
            throw error;
        }
    }

    /**
     * Disable two-factor authentication
     */
    static async disableTwoFactor(): Promise<{message: string}> {
        try {
            return await apiClient.post<{message: string}>('/api/2FA/disable');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get current user info
     */
    static async getCurrentUser(): Promise<UserInfo> {
        try {
            return await apiClient.get<UserInfo>('/api/user/profile');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update user profile
     */
    static async updateProfile(updates: Partial<UserInfo>): Promise<UserInfo> {
        try {
            return await apiClient.patch<UserInfo>('/api/user/profile', updates);
        } catch (error) {
            throw error;
        }
    }
}

export default AuthService;
